/**
 * Shaping Rio's interviewer for a job: the card, the brief to Rio, and the
 * candidate's-eye preview, laid out side by side on a wide screen and as one
 * stack (card, brief, preview) on a narrow one.
 *
 * Used while posting a job and on a job's Interviewer tab. The state lives in
 * useInterviewer, owned by the page, so posting and saving stay the page's.
 */
import type { ReactNode } from 'react';
import { Button } from 'antd';
import type { InterviewerState } from '../../interviewer/useInterviewer';
import type { Posting } from '../../services/interviewer.types';
import InterviewerCard from './InterviewerCard';
import BriefRio from './BriefRio';
import CandidatePreview from './CandidatePreview';
import styles from './index.module.css';

interface Props {
  state: InterviewerState;
  posting: Posting;
  /** The card's status label, e.g. "Drafted from your posting". */
  status: string;
  notice?: ReactNode;
  /** Shown when there's no posting to draft from yet. */
  emptyMessage?: string;
}

const Skeleton = () => (
  <div className={styles.layout} aria-busy="true">
    <div className={styles.panel}>
      <div className={styles.skeleton}>
        <p className={styles.hint} role="status">
          Rio is drafting your interviewer from the posting…
        </p>
        <span className={styles.bone} style={{ width: '40%' }} />
        <span className={`${styles.bone} ${styles.boneTall}`} />
        <span className={`${styles.bone} ${styles.boneTall}`} />
        <span className={`${styles.bone} ${styles.boneTall}`} />
        <span className={styles.bone} style={{ width: '70%' }} />
      </div>
    </div>
    <div className={styles.side}>
      <div className={styles.panel}>
        <div className={styles.skeleton}>
          <span className={styles.bone} style={{ width: '55%' }} />
          <span className={`${styles.bone} ${styles.boneTall}`} />
        </div>
      </div>
      <div className={styles.panel}>
        <div className={styles.skeleton}>
          <span className={styles.bone} style={{ width: '45%' }} />
          <span className={styles.bone} />
          <span className={styles.bone} style={{ width: '80%' }} />
        </div>
      </div>
    </div>
  </div>
);

const InterviewerSetup = ({
  state,
  posting,
  status,
  notice,
  emptyMessage,
}: Props) => {
  if (!state.config || !state.preview) {
    if (state.status === 'error') {
      return (
        <div className={styles.panel}>
          <div className={styles.state} role="alert">
            <p className={styles.stateTitle}>
              Rio couldn&rsquo;t draft the interviewer.
            </p>
            <p className={styles.hint}>{state.draftError}</p>
            <Button shape="round" onClick={() => state.draft(posting)}>
              Try again
            </Button>
          </div>
        </div>
      );
    }
    if (state.status === 'drafting') return <Skeleton />;
    return (
      <div className={styles.panel}>
        <div className={styles.state}>
          <p className={styles.stateTitle}>
            Rio drafts your interviewer from the posting.
          </p>
          <p className={styles.hint}>
            {emptyMessage ??
              'Add a job title and a description, and Rio will draft it here.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <InterviewerCard
        config={state.config}
        preview={state.preview}
        skills={posting.skills}
        status={status}
        disabled={state.busy}
        highlight={state.highlight}
        onChange={state.update}
        notice={notice}
      />
      <div className={styles.side}>
        <BriefRio
          messages={state.messages}
          busy={state.busy}
          disabled={false}
          onSend={state.send}
          onKeep={state.keep}
          onUndo={state.undo}
        />
        <CandidatePreview preview={state.preview} />
      </div>
    </div>
  );
};

export default InterviewerSetup;
export { styles as interviewerStyles };
